import Flex from "common/Boxes/Flex";
import React, { useContext } from "react";
import useMedia from "utils/hooks/useMedia";
import { TradeDataWithKey } from "@shared/types";
import TradeCard from "features/trades/TradesCards/TradeCard";
import { useTradesCardsAndSorters } from "features/trades/common/commonHooks";
import usePagination from "common/Pagination/usePagination";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useTheme } from "styled-components";
import SocketPnlProvider from "@shared/features/dashboard/SocketPnlProvider";
import { skipToken } from "@reduxjs/toolkit/query";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import NewLookSortersWrapper from "features/trades/NewLook/NewLookSortersWrapper.tsx";

interface TradesCardsListProps {
  data: TradeDataWithKey[];
  loading: boolean;
  isOpenTrades: boolean;
  emptyState?: React.JSX.Element;
  allowTradeClick?: boolean;
  shouldFetchSocketPnl?: boolean;
}

const TradesCardsList = ({
  data,
  loading,
  isOpenTrades,
  emptyState,
  allowTradeClick,
  shouldFetchSocketPnl = true,
}: TradesCardsListProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const theme = useTheme();
  const shouldShowTooltip = useMedia([`(min-width: 455px)`], [true], false);
  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useTradesCardsAndSorters({ data, isOpenTrades, shouldShowTooltip });

  const [pagination, paginatedData] = usePagination({
    data: sortedData || [],
  });

  return (
    <SocketPnlProvider getTradesArgs={skipToken} openTradesData={paginatedData}>
      <div style={{ position: "relative" }}>
        <div style={{ marginBottom: theme.isNewLook ? 4 : 12 }}>
          {theme.isNewLook ? (
            <NewLookSortersWrapper>{cardsSorters}</NewLookSortersWrapper>
          ) : (
            cardsSorters
          )}
        </div>
        <LoadingSpinnerOverlay
          isLoading={loading}
          spinnerMarginTop={40}
          backgroundColor={
            theme.colors[isDrawer ? "layoutBgColor" : "cardBgColor"]
          }
        >
          {paginatedData?.map((trade) => {
            return (
              <div style={{ padding: "6px 0" }} key={trade.id}>
                <TradeCard
                  tradeData={trade}
                  isOpenTrades={isOpenTrades}
                  toggleExpanded={toggleExpanded}
                  isExpanded={isExpanded(trade?.key)}
                  allowTradeClick={allowTradeClick}
                  shouldFetchSocketPnl={shouldFetchSocketPnl}
                />
              </div>
            );
          })}
          {!loading && sortedData?.length === 0 && emptyState}
          <Flex $justifyContent="flex-end" style={{ marginTop: 12 }}>
            {pagination}
          </Flex>
        </LoadingSpinnerOverlay>
      </div>
    </SocketPnlProvider>
  );
};

export default TradesCardsList;
